import { put, takeEvery, select, call, all, take, fork, cancel, join } from 'redux-saga/effects';
import { END } from 'redux-saga';
import hash from 'object-hash';
import {
  CASE_UPLOAD_FILES_BEGIN,
  CASE_UPLOAD_FILES_SUCCESS,
  CASE_UPLOAD_FILES_FAILURE,
  CASE_UPLOAD_FILES_PROGRESS,
  CASE_UPLOAD_FILES_DISMISS_FEEDBACK,
  CASE_UPLOAD_FILES_REJECT,
  CASE_UPLOAD_FILE_TOO_LARGE,
  CASE_UPLOAD_OVERSIZED_FILE,
  CASE_UPLOAD_FILES_DISMISS_TOO_LARGE_FEEDBACK,
  CASE_ASSIGN_DOCUMENTS_SUCCESS,
  CASE_UPLOAD_FILES_CLOSE_PROGRESS,
  CASE_CANCEL_UPLOAD_FILES,
  CASE_FETCH_FOLDER_BEGIN,
  CASE_UPLOAD_DUPLICATE_FILE,
  CASE_FETCH_DOCUMENTS_BEGIN,
} from './constants';
import api from 'common/api';
import {
  withCurrentCaseId,
  selectUploadFiles,
  selectCurrentFolder,
  selectQuickUploadFileSizeLimit,
} from '../../../common/selectors';
import { replaceItemImmutable, addItemImmutable } from 'utils/arrays';
import { v4 as uuidv4 } from 'uuid';

const activeUploadTasks = new Set();

export function uploadFiles(files, folderId, uploadInnerFile = false, batchId, lateInsertTabMapping) {
  // If need to pass args to saga, pass it with the begin action.
  return {
    type: CASE_UPLOAD_FILES_BEGIN,
    payload: { files, folderId, uploadInnerFile, batchId, lateInsertTabMapping },
  };
}

export function dismissUploadFilesFeedback() {
  return {
    type: CASE_UPLOAD_FILES_DISMISS_FEEDBACK,
  };
}

export function closeUploadFilesProgress() {
  return {
    type: CASE_UPLOAD_FILES_CLOSE_PROGRESS,
  };
}

export function cancelUploadFiles() {
  return {
    type: CASE_CANCEL_UPLOAD_FILES,
  };
}

const getFileProps = (file) => ({
  path: file.path,
  name: file.name,
  lastModified: file.lastModified,
  size: file.size,
  type: file.type,
});

export const prepareFile = (file) => {
  const meta = new Blob([JSON.stringify(getFileProps(file))], {
    type: 'application/json',
  });

  const formData = new FormData();
  formData.append('', file);
  formData.append('', meta);

  return formData;
};

// worker Saga: will be fired on CASE_UPLOAD_FILE_BEGIN actions
function* uploadFile({
  payload: {
    file,
    folderId,
    uploadInnerFile,
    fetchFolder,
    caseId,
    batchId,
    lateInsertTabMapping,
  },
}) {
  const fileProps = getFileProps(file);
  const filePropsWithHash = { hash: hash(fileProps), ...fileProps };
  const files = yield select(selectUploadFiles);
  const index = files.findIndex((x) => x.hash === filePropsWithHash.hash);

  if (index > -1) {
    yield put({
      type: CASE_UPLOAD_FILES_REJECT,
    });
    return;
  }
  const fileToSend = prepareFile(file);
  if (lateInsertTabMapping) {
    fileToSend.append('lateInsertTabMapping', JSON.stringify(lateInsertTabMapping));
  }
  const searchParams = new URLSearchParams();
  if (uploadInnerFile) searchParams.append('ring', 'inner');
  if (batchId) searchParams.append('batchId', batchId);
  if (lateInsertTabMapping) {
    const tabValue = lateInsertTabMapping[file.name];
    if (tabValue !== undefined) {
      searchParams.append('lateInsertTab', `${tabValue}`);
    }
  }
  const queryString = searchParams.toString();
  const basePath = folderId
    ? `/cases/${caseId}/folders/${folderId}/files`
    : `/cases/${caseId}/files`;

  const channel = yield call(
    api.createUploadFileChannel,
    'POST',
    `${basePath}${queryString ? `?${queryString}` : ''}`,
    fileToSend,
  );

  try {
    while (true) {
      const result = yield take(channel);
      if (result === END) {
        break;
      }
      const { progress = 0, error, success, response: document } = result;

      if (error) {
        if (error.status === 409 && folderId) {
          yield put({
            type: CASE_UPLOAD_DUPLICATE_FILE,
            data: { file: { error, ...filePropsWithHash, progress: '100.00' }, error },
          });
        } else {
          yield put({
            type: CASE_UPLOAD_FILES_FAILURE,
            data: { file: { error, ...filePropsWithHash, progress: '100.00' }, error },
          });
        }

        if (fetchFolder && folderId) {
          yield put({ type: CASE_FETCH_FOLDER_BEGIN, payload: { folderId, noPromise: true } });
          yield put({ type: CASE_FETCH_DOCUMENTS_BEGIN });
        }
        return;
      }

      if (success) {
        const currentFolder = yield select(selectCurrentFolder);
        const shouldAssign =
          (folderId && folderId === currentFolder.id) || Object.keys(currentFolder).length === 0;

        yield put({
          type: CASE_ASSIGN_DOCUMENTS_SUCCESS,
          data: {
            document,
            shouldAssign,
            zeroBasedIndex: currentFolder && currentFolder.zeroBasedIndex,
          },
        });

        yield put({
          type: CASE_UPLOAD_FILES_SUCCESS,
          data: { file: { ...filePropsWithHash, ...document, progress: '100.00' } },
        });
        if (fetchFolder && folderId) {
          yield put({ type: CASE_FETCH_FOLDER_BEGIN, payload: { folderId, noPromise: true } });
          yield put({ type: CASE_FETCH_DOCUMENTS_BEGIN });
        }
        return;
      }

      yield put({
        type: CASE_UPLOAD_FILES_PROGRESS,
        data: { file: { progress, ...filePropsWithHash } },
      });
    }
  } finally {
    channel.close();
  }
}

// worker Saga: will be fired on CASE_UPLOAD_FILE_BEGIN actions
export function* doUploadFiles({
  payload: { files, folderId, uploadInnerFile, batchId, lateInsertTabMapping },
}) {
  // Get the configurable file size limit from case settings
  const quickUploadFileSizeLimit = yield select(selectQuickUploadFileSizeLimit);
  const fileSizeLimitMB = quickUploadFileSizeLimit || 100; // Default to 100MB if not set
  const fileSizeLimitBytes = fileSizeLimitMB * 1024 * 1024;

  const largeFiles = files.filter((file) => file.size >= fileSizeLimitBytes);
  const uploadableFiles = files.filter((file) => file.size < fileSizeLimitBytes);

  // Add large files to the upload list immediately with error status (like duplicates)
  if (largeFiles.length > 0) {
    for (const file of largeFiles) {
      const fileProps = getFileProps(file);
      const filePropsWithHash = { hash: hash(fileProps), ...fileProps };

      yield put({
        type: CASE_UPLOAD_OVERSIZED_FILE,
        data: {
          file: {
            error: {
              status: 413,
              statusText: 'File Size too large',
              body: `File exceeds ${fileSizeLimitMB}MB limit. Please use bulk upload for larger files.`,
            },
            ...filePropsWithHash,
            progress: '100.00',
          },
          error: {
            status: 413,
            statusText: 'File Size too large',
            body: `File exceeds ${fileSizeLimitMB}MB limit. Please use bulk upload for larger files.`,
          },
        },
      });
    }
  }

  // If no uploadable files remain, we're done
  if (uploadableFiles.length === 0) {
    return;
  }
  const tasks = yield all(
    uploadableFiles.map((file, idx) =>
      fork(withCurrentCaseId(uploadFile), {
        payload: {
          file,
          folderId,
          uploadInnerFile,
          fetchFolder: uploadableFiles.length - 1 === idx,
          batchId,
          lateInsertTabMapping,
        },
      }),
    ),
  );

  const taskList = Array.isArray(tasks) ? tasks : [tasks];

  taskList.forEach((task) => activeUploadTasks.add(task));
  try {
    yield all(taskList.map((task) => join(task)));
  } finally {
    taskList.forEach((task) => activeUploadTasks.delete(task));
  }
}

/*
  Alternatively you may use takeEvery.

  takeLatest does not allow concurrent requests. If an action gets
  dispatched while another is already pending, that pending one is cancelled
  and only the latest one will be run.
*/
export function* watchUploadFiles() {
  yield takeEvery(CASE_UPLOAD_FILES_BEGIN, doUploadFiles);
}

export function* doCancelUploadFiles() {
  const tasks = Array.from(activeUploadTasks);
  for (const task of tasks) {
    if (task && task.isRunning && task.isRunning()) {
      yield cancel(task);
    }
    activeUploadTasks.delete(task);
  }
}

export function* watchCancelUploadFiles() {
  yield takeEvery(CASE_CANCEL_UPLOAD_FILES, doCancelUploadFiles);
}

// Redux reducer
export function reducer(state, action) {
  const clearFilesAndFeedbacks = (shouldClear = true) =>
    shouldClear && {
      uploadFilesPending: 0,
      uploadFiles: [],
      uploadFilesFeedback: null,
    };

  switch (action.type) {
    case CASE_UPLOAD_FILES_BEGIN:
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending + action.payload.files.length,
        uploadFilesProgressClosed: false,
      };

    case CASE_UPLOAD_FILES_SUCCESS:
      const indexS = state.uploadFiles.findIndex((x) => x.hash === action.data.file.hash);
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - 1,
        uploadFiles:
          indexS < 0
            ? addItemImmutable(state.uploadFiles, action.data.file)
            : replaceItemImmutable(state.uploadFiles, action.data.file, indexS),
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    case CASE_UPLOAD_FILES_PROGRESS:
      const indexP = state.uploadFiles.findIndex((x) => x.hash === action.data.file.hash);
      return {
        ...state,
        uploadFiles:
          indexP < 0
            ? addItemImmutable(state.uploadFiles, action.data.file)
            : replaceItemImmutable(state.uploadFiles, action.data.file, indexP),
      };

    case CASE_UPLOAD_FILES_FAILURE:
      const indexF = state.uploadFiles.findIndex((x) => x.hash === action.data.file.hash);
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - 1,
        uploadFilesFeedback: {
          message: 'feedback.uploadFilesFailure',
          error: typeof action.data.error.body === 'string' && action.data.error,
          warning: true,
        },
        uploadFiles:
          indexF < 0
            ? addItemImmutable(state.uploadFiles, action.data.file)
            : replaceItemImmutable(state.uploadFiles, action.data.file, indexF),
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    case CASE_UPLOAD_DUPLICATE_FILE:
      const indexD = state.uploadFiles.findIndex((x) => x.hash === action.data.file.hash);
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - 1,
        uploadFilesFeedback: null,
        uploadFiles:
          indexD < 0
            ? addItemImmutable(state.uploadFiles, action.data.file)
            : replaceItemImmutable(state.uploadFiles, action.data.file, indexD),
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    case CASE_UPLOAD_OVERSIZED_FILE:
      const indexO = state.uploadFiles.findIndex((x) => x.hash === action.data.file.hash);
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - 1,
        uploadFilesFeedback: null,
        uploadFiles:
          indexO < 0
            ? addItemImmutable(state.uploadFiles, action.data.file)
            : replaceItemImmutable(state.uploadFiles, action.data.file, indexO),
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    case CASE_UPLOAD_FILES_DISMISS_FEEDBACK:
      return {
        ...state,
        uploadFilesPending: 0,
        uploadFiles: [],
        uploadFilesFeedback: null,
        uploadFilesTooLargeFeedback: null,
      };

    case CASE_UPLOAD_FILES_CLOSE_PROGRESS:
      return {
        ...state,
        uploadFilesProgressClosed: true,
        ...clearFilesAndFeedbacks(state.uploadFilesPending === 0),
      };

    case CASE_CANCEL_UPLOAD_FILES:
      return {
        ...state,
        uploadFilesPending: 0,
        uploadFiles: [],
        uploadFilesFeedback: null,
        uploadFilesTooLargeFeedback: null,
        uploadFilesProgressClosed: true,
      };

    case CASE_UPLOAD_FILES_REJECT:
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - 1,
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    case CASE_UPLOAD_FILE_TOO_LARGE:
      return {
        ...state,
        uploadFilesTooLargeFeedback: action.feedback,
      };

    case CASE_UPLOAD_FILES_DISMISS_TOO_LARGE_FEEDBACK:
      return {
        ...state,
        uploadFilesPending: state.uploadFilesPending - action.data.length,
        uploadFilesTooLargeFeedback: null,
        ...clearFilesAndFeedbacks(
          state.uploadFilesProgressClosed && state.uploadFilesPending - 1 === 0,
        ),
      };

    default:
      return state;
  }
}
